import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Clock, CheckCircle, AlertCircle, FileText, Loader2, LogOut, Camera, Wrench, X } from 'lucide-react';
import { toast } from 'sonner';
import { useRef } from 'react';
import { api } from '../../utils/api';
import { StatusBadge, PriorityBadge, ServiceIcon, LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TechnicianDashboard = () => {
  const { t, lang } = useLang();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockStatus, setClockStatus] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [reportDue, setReportDue] = useState(false);
  const [reportMinutesLeft, setReportMinutesLeft] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ description: '', file: null, preview: null, jobId: '' });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [hoursSummary, setHoursSummary] = useState(null);
  const locationIntervalRef = useRef(null);
  const timerRef = useRef(null);
  const clockRef = useRef(null);
  const reportCheckRef = useRef(null);

  const REPORT_INTERVAL_HOURS = 4;
  const REPORT_INTERVAL_MS = REPORT_INTERVAL_HOURS * 60 * 60 * 1000;

  useEffect(() => {
    loadData();
    clockRef.current = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      if (locationIntervalRef.current) clearInterval(locationIntervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (clockRef.current) clearInterval(clockRef.current);
      if (reportCheckRef.current) clearInterval(reportCheckRef.current);
    };
  }, []);

  useEffect(() => {
    if (clockStatus?.status === 'working') { startLocationTracking(); startTimer(); startReportCheck(); }
    else if (clockStatus?.status === 'lunch') { stopLocationTracking(); stopReportCheck(); }
    else { stopLocationTracking(); stopTimer(); stopReportCheck(); setReportDue(false); }
    return () => { stopLocationTracking(); stopTimer(); stopReportCheck(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockStatus?.status, clockStatus?.last_report_at]);

  const checkReportDue = () => {
    if (!clockStatus?.session?.clock_in) return;
    const now = new Date();
    const lastReport = clockStatus?.last_report_at ? new Date(clockStatus.last_report_at) : null;
    const clockIn = new Date(clockStatus.session.clock_in);
    const baseline = lastReport && lastReport > clockIn ? lastReport : clockIn;
    const elapsed = now - baseline;
    const remaining = REPORT_INTERVAL_MS - elapsed;
    if (remaining <= 0) { setReportDue(true); setReportMinutesLeft(0); }
    else { setReportDue(false); setReportMinutesLeft(Math.ceil(remaining / 60000)); }
  };

  const startReportCheck = () => { if (reportCheckRef.current) clearInterval(reportCheckRef.current); checkReportDue(); reportCheckRef.current = setInterval(checkReportDue, 30000); };
  const stopReportCheck = () => { if (reportCheckRef.current) { clearInterval(reportCheckRef.current); reportCheckRef.current = null; } };

  const loadData = async () => {
    try {
      const [jobsData, statusData, hoursData] = await Promise.all([
        api('/api/service-requests'),
        api('/api/clock/status'),
        api('/api/technician/my-hours').catch(() => null),
      ]);
      setJobs(jobsData); setClockStatus(statusData);
      if (hoursData) setHoursSummary(hoursData);
      if (statusData.session?.clock_in) { const clockIn = new Date(statusData.session.clock_in); setElapsedTime(Math.floor((new Date() - clockIn) / 1000)); }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (!reportForm.description || !reportForm.file || !reportForm.jobId) { toast.error('Ju lutem plotesoni te gjitha fushat: zgjidhni punen, shkruani pershkrimin dhe ngarkoni foton.'); return; }
    setSubmittingReport(true);
    try {
      const formData = new FormData(); formData.append('file', reportForm.file);
      const token = localStorage.getItem('token');
      const uploadRes = await fetch(`${API_URL}/api/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (!uploadRes.ok) throw new Error('Photo upload failed');
      const uploadData = await uploadRes.json();
      await api('/api/work-logs', { method: 'POST', body: JSON.stringify({ request_id: reportForm.jobId, service_performed: '4-Hour Progress Report', description: reportForm.description }) });
      await api('/api/work-photos', { method: 'POST', body: JSON.stringify({ request_id: reportForm.jobId, image_url: uploadData.url, caption: `4h Report: ${reportForm.description.substring(0, 80)}` }) });
      toast.success('Raporti u dergua me sukses!');
      setShowReportModal(false); setReportForm({ description: '', file: null, preview: null, jobId: '' }); setReportDue(false); loadData();
    } catch (err) { toast.error(err.message || 'Deshtoi dergimi i raportit'); }
    finally { setSubmittingReport(false); }
  };

  const startTimer = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000); };
  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) reject(new Error('Geolocation not supported'));
    navigator.geolocation.getCurrentPosition(pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }), err => reject(err), { enableHighAccuracy: true, timeout: 10000 });
  });

  const startLocationTracking = () => {
    if (locationIntervalRef.current) return;
    const sendLocation = async () => { try { const loc = await getLocation(); await api('/api/clock/location', { method: 'POST', body: JSON.stringify(loc) }); } catch (e) { console.warn('Location update failed', e); } };
    sendLocation(); locationIntervalRef.current = setInterval(sendLocation, 60000);
  };
  const stopLocationTracking = () => { if (locationIntervalRef.current) { clearInterval(locationIntervalRef.current); locationIntervalRef.current = null; } };

  const handleClockIn = async () => {
    setClockLoading(true);
    try { const loc = await getLocation(); await api('/api/clock/start', { method: 'POST', body: JSON.stringify(loc) }); toast.success('Clocked in! Work started.'); setElapsedTime(0); loadData(); }
    catch (err) { if (err.message?.includes('Geolocation') || err.message?.includes('denied')) toast.error('Please allow location access to clock in.'); else toast.error(err.message); }
    finally { setClockLoading(false); }
  };

  const handleLunch = async () => { setClockLoading(true); try { await api('/api/clock/lunch', { method: 'POST' }); toast.success('Lunch break started. Enjoy!'); loadData(); } catch (err) { toast.error(err.message); } finally { setClockLoading(false); } };
  const handleBackFromLunch = async () => { setClockLoading(true); try { await api('/api/clock/back', { method: 'POST' }); toast.success('Welcome back! Work resumed.'); loadData(); } catch (err) { toast.error(err.message); } finally { setClockLoading(false); } };
  const handleClockOut = async () => { if (!window.confirm('Are you sure you want to finish work for today?')) return; setClockLoading(true); try { const result = await api('/api/clock/finish', { method: 'POST' }); toast.success(result.message); loadData(); } catch (err) { toast.error(err.message); } finally { setClockLoading(false); } };

  const formatTime = (seconds) => { const h = Math.floor(seconds / 3600); const m = Math.floor((seconds % 3600) / 60); const s = seconds % 60; return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`; };

  if (loading) return <LoadingSpinner />;

  const clockInTime = clockStatus?.session?.clock_in ? new Date(clockStatus.session.clock_in) : null;
  const status = clockStatus?.status || 'off';
  const assignedJobs = jobs.filter(j => j.status === 'assigned');
  const inProgressJobs = jobs.filter(j => j.status === 'in_progress');
  const completedJobs = jobs.filter(j => j.status === 'completed');

  return (
    <div data-testid="technician-dashboard" className="space-y-6">
      <div className="flex items-center justify-between">
        <div><p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">{t('staff_portal')}</p><h1 className="font-heading text-3xl font-bold text-white">{t('tech_dashboard')}</h1></div>
        <div className="text-right">
          <p className="font-mono text-2xl text-white font-bold" data-testid="live-clock">{currentTime.toLocaleTimeString('en-US', { hour12: false })}</p>
          <p className="text-xs text-gray-500">{currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      <div className="rounded-xl bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border border-gray-700/50 overflow-hidden" data-testid="clock-card">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${status === 'working' ? 'bg-green-500 animate-pulse' : status === 'lunch' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-600'}`}></div>
            <h2 className="text-sm font-bold text-white tracking-wide">TIME CLOCK</h2>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider ${status === 'working' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : status === 'lunch' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : status === 'finished' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'bg-gray-700/20 text-gray-500 border border-gray-600/30'}`} data-testid="clock-status">
            {status === 'working' ? 'WORKING' : status === 'lunch' ? 'LUNCH BREAK' : status === 'finished' ? 'SHIFT COMPLETE' : 'OFF DUTY'}
          </div>
        </div>
        <div className="p-6">
          {(status === 'working' || status === 'lunch') && (
            <div className="text-center mb-6">
              <p className={`font-mono text-6xl font-bold tracking-wider ${status === 'lunch' ? 'text-yellow-400' : 'text-green-400'}`} data-testid="clock-timer">{formatTime(elapsedTime)}</p>
              <div className="mt-3 flex items-center justify-center gap-6 text-sm">
                <div><p className="text-gray-600 text-xs">Clock In</p><p className="text-white font-mono">{clockInTime?.toLocaleTimeString('en-US', { hour12: false })}</p></div>
                <div className="w-px h-8 bg-gray-700"></div>
                <div><p className="text-gray-600 text-xs">Date</p><p className="text-white font-mono">{clockInTime?.toLocaleDateString()}</p></div>
                <div className="w-px h-8 bg-gray-700"></div>
                <div><p className="text-gray-600 text-xs">Status</p><p className={`font-medium ${status === 'lunch' ? 'text-yellow-400' : 'text-green-400'}`}>{status === 'lunch' ? 'Paused' : 'Active'}</p></div>
              </div>
            </div>
          )}
          {(status === 'off' || status === 'finished') && (
            <div className="text-center mb-6 py-4">
              <p className="font-mono text-5xl font-bold text-gray-700 tracking-wider mb-2">00:00:00</p>
              <p className="text-sm text-gray-500">{status === 'finished' ? 'Shift complete for today. Great work!' : 'Press Start Work to begin your shift'}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            {status === 'off' || status === 'finished' ? (
              <button onClick={handleClockIn} disabled={clockLoading} className="bg-green-600 hover:bg-green-700 text-white text-lg px-10 py-4 rounded-lg font-bold flex items-center gap-3 transition-all shadow-lg shadow-green-600/20" data-testid="clock-in-btn">
                {clockLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}{t('clock_in')}
              </button>
            ) : status === 'working' ? (
              <>
                <button onClick={handleLunch} disabled={clockLoading} className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3.5 rounded-lg font-bold flex items-center gap-2 transition-all" data-testid="lunch-btn">{clockLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}{t('start_break')}</button>
                <button onClick={handleClockOut} disabled={clockLoading} className="bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 rounded-lg font-bold flex items-center gap-2 transition-all" data-testid="clock-out-btn">{clockLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}{t('clock_out')}</button>
              </>
            ) : status === 'lunch' ? (
              <>
                <button onClick={handleBackFromLunch} disabled={clockLoading} className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-3.5 rounded-lg font-bold flex items-center gap-2 transition-all" data-testid="back-lunch-btn">{clockLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}{t('end_break')}</button>
                <button onClick={handleClockOut} disabled={clockLoading} className="bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 rounded-lg font-bold flex items-center gap-2 transition-all" data-testid="clock-out-btn">{t('clock_out')}</button>
              </>
            ) : null}
          </div>
          {status === 'working' && <p className="text-xs text-green-500/50 text-center mt-4 flex items-center justify-center gap-1.5"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>GPS location tracking active</p>}
        </div>
      </div>

      {reportDue && status === 'working' && (
        <div className="rounded-xl bg-gradient-to-r from-red-900/80 to-orange-900/60 border-2 border-red-500/50 p-5 animate-pulse" data-testid="report-due-banner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center shrink-0"><AlertCircle className="w-6 h-6 text-red-400" /></div>
            <div className="flex-1"><h3 className="text-lg font-bold text-red-300">Raportim i Detyrueshm</h3><p className="text-sm text-red-200/80 mt-0.5">Kane kaluar 4 ore pa raportim. Dergoni perditesimin tuaj me tekst dhe foto.</p></div>
            <button onClick={() => { setShowReportModal(true); setReportForm({ description: '', file: null, preview: null, jobId: inProgressJobs[0]?.id || assignedJobs[0]?.id || '' }); }} className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-bold text-sm flex items-center gap-2 transition-all shadow-lg shadow-red-500/30 shrink-0" data-testid="open-report-modal-btn"><FileText className="w-4 h-4" />Raporto Tani</button>
          </div>
        </div>
      )}

      {!reportDue && status === 'working' && reportMinutesLeft !== null && (
        <div className="rounded-lg bg-gray-900/50 border border-gray-800 px-4 py-3 flex items-center justify-between" data-testid="report-countdown">
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-gray-500" /><p className="text-xs text-gray-400">Raporti tjeter per: <span className="text-white font-mono font-bold">{Math.floor(reportMinutesLeft / 60)}h {reportMinutesLeft % 60}m</span></p></div>
          <button onClick={() => { setShowReportModal(true); setReportForm({ description: '', file: null, preview: null, jobId: inProgressJobs[0]?.id || assignedJobs[0]?.id || '' }); }} className="text-xs text-red-400 hover:text-red-300 font-medium" data-testid="early-report-btn">Raporto Heret</button>
        </div>
      )}

      {showReportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="report-modal-overlay">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-lg w-full" data-testid="report-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center"><FileText className="w-5 h-5 text-red-400" /></div>
              <div><h3 className="text-lg font-bold text-white">Raporti i Progresit (4-Oresh)</h3><p className="text-xs text-gray-500">Tekst + Foto te detyrueshme</p></div>
              {!reportDue && <button onClick={() => setShowReportModal(false)} className="ml-auto p-1.5 rounded-lg hover:bg-gray-800"><X className="w-5 h-5 text-gray-400" /></button>}
            </div>
            <form onSubmit={submitReport} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Zgjidhni punen</label>
                <select className="input w-full" value={reportForm.jobId} onChange={(e) => setReportForm({ ...reportForm, jobId: e.target.value })} required data-testid="report-job-select">
                  <option value="">-- Zgjidhni --</option>
                  {[...inProgressJobs, ...assignedJobs].map(j => (<option key={j.id} value={j.id}>{j.ticket_number} - {j.service_type.replace('_', ' ').toUpperCase()}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Cfare keni bere?</label>
                <textarea className="input min-h-[100px] w-full" placeholder="Pershkruani punen e bere ne 4 oret e fundit..." value={reportForm.description} onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })} required data-testid="report-description-input" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Foto e punes (e detyrueshme)</label>
                <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center hover:border-red-500 transition-colors cursor-pointer relative">
                  <input type="file" accept="image/*,.heic,.heif,.pdf,.doc,.docx" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => { const file = e.target.files[0]; if (file) setReportForm({ ...reportForm, file, preview: URL.createObjectURL(file) }); }} data-testid="report-photo-input" />
                  {reportForm.preview ? <img src={reportForm.preview} alt="Preview" className="w-full h-32 object-cover rounded" /> : <div><Camera className="w-8 h-8 mx-auto mb-1 text-gray-600" /><p className="text-sm text-gray-400">Shtypni per foto</p></div>}
                </div>
              </div>
              <button type="submit" disabled={submittingReport || !reportForm.description || !reportForm.file || !reportForm.jobId} className="btn-primary w-full py-3 font-bold disabled:opacity-50" data-testid="submit-report-btn">
                {submittingReport && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}Dergo Raportin
              </button>
              {reportDue && <p className="text-xs text-red-400 text-center">Ky raportim eshte i detyrueshm. Ju lutem plotesoni para se te vazhdoni punen.</p>}
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="nyc-hours-summary">
        <div className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-4">
          <p className="text-[10px] text-gray-500 tracking-widest mb-1">{lang === 'sq' ? 'SOT' : lang === 'es' ? 'HOY' : 'TODAY'}</p>
          <p className="text-2xl font-bold text-white font-mono" data-testid="hours-today">
            {(() => {
              const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
              const h = hoursSummary?.daily_breakdown?.[today] || 0;
              return `${h.toFixed(2)}h`;
            })()}
          </p>
          <p className="text-[10px] text-gray-600 mt-1">{new Date().toLocaleDateString(lang === 'sq' ? 'sq' : lang === 'es' ? 'es' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
        </div>
        <div className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-4">
          <p className="text-[10px] text-gray-500 tracking-widest mb-1">{lang === 'sq' ? 'KËTË JAVË' : lang === 'es' ? 'ESTA SEMANA' : 'THIS WEEK'}</p>
          <p className="text-2xl font-bold text-white font-mono" data-testid="hours-week">
            {(hoursSummary?.total_hours || 0).toFixed(2)}h
          </p>
          <p className="text-[10px] text-gray-600 mt-1">{hoursSummary?.total_sessions || 0} {lang === 'sq' ? 'seanca' : lang === 'es' ? 'sesiones' : 'sessions'}</p>
        </div>
        <div className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-4">
          <p className="text-[10px] text-gray-500 tracking-widest mb-1">{lang === 'sq' ? 'NORMALE' : 'REGULAR'}</p>
          <p className="text-2xl font-bold text-blue-400 font-mono" data-testid="hours-regular">
            {(hoursSummary?.regular_hours || 0).toFixed(2)}h
          </p>
          <p className="text-[10px] text-gray-600 mt-1">{lang === 'sq' ? '≤ 40 orë/javë' : '≤ 40h/week'}</p>
        </div>
        <div className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-4">
          <p className="text-[10px] text-gray-500 tracking-widest mb-1">{lang === 'sq' ? 'JASHTË ORARIT' : lang === 'es' ? 'HORAS EXTRA' : 'OVERTIME'}</p>
          <p className={`text-2xl font-bold font-mono ${(hoursSummary?.overtime_hours || 0) > 0 ? 'text-orange-400' : 'text-gray-600'}`} data-testid="hours-overtime">
            {(hoursSummary?.overtime_hours || 0).toFixed(2)}h
          </p>
          <p className="text-[10px] text-gray-600 mt-1">{lang === 'sq' ? '1.5× tarifa · Ligji NYS' : lang === 'es' ? '1.5× tarifa · Ley NYS' : '1.5× rate · NYS Labor Law'}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg bg-gray-900 border border-gray-800 p-4 text-center"><p className="text-2xl font-bold text-blue-400">{assignedJobs.length}</p><p className="text-xs text-gray-500 mt-1">Assigned</p></div>
        <div className="rounded-lg bg-gray-900 border border-gray-800 p-4 text-center"><p className="text-2xl font-bold text-yellow-400">{inProgressJobs.length}</p><p className="text-xs text-gray-500 mt-1">In Progress</p></div>
        <div className="rounded-lg bg-gray-900 border border-gray-800 p-4 text-center"><p className="text-2xl font-bold text-green-400">{completedJobs.length}</p><p className="text-xs text-gray-500 mt-1">Completed</p></div>
      </div>

      <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800"><h2 className="text-sm font-bold text-white tracking-wide">Active Jobs</h2></div>
        {[...inProgressJobs, ...assignedJobs].length === 0 ? (
          <div className="p-8 text-center"><Wrench className="w-8 h-8 mx-auto mb-2 text-gray-700" /><p className="text-sm text-gray-600">No active jobs assigned.</p></div>
        ) : (
          <div className="divide-y divide-gray-800/50">
            {[...inProgressJobs, ...assignedJobs].map((job) => (
              <div key={job.id} className="px-5 py-4 hover:bg-gray-800/30 transition-colors" data-testid={`job-item-${job.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><p className="font-mono text-xs text-gray-600">{job.ticket_number}</p><StatusBadge status={job.status} /><PriorityBadge priority={job.priority} /></div>
                    <p className="text-sm font-medium text-white"><ServiceIcon type={job.service_type} className="w-4 h-4 inline mr-1.5" />{job.service_type.replace('_', ' ').toUpperCase()}</p>
                    {job.address && <p className="text-xs text-gray-500 mt-1">{job.address}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicianDashboard;
